import { useTranslation } from "react-i18next";
import type { PlatformTypeName, ReportMessageSummaryResponse } from "@multi-chat/shared";
import { ChannelIdentityBadge } from "../../components/channel/ChannelIdentityBadge.js";
import { channelLabelI18nKey } from "../../components/channel/channel-presentation.js";
import { cn } from "../../lib/utils.js";
import {
  INCOMING_REPORT_METRIC_CARDS,
  incomingMetricCount,
} from "./incoming-report-presentation.js";

export function IncomingReportSummary({
  summary,
  platform,
  onSelectPlatform,
}: {
  summary: ReportMessageSummaryResponse;
  platform?: PlatformTypeName;
  onSelectPlatform: (platform: PlatformTypeName | null) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();

  return (
    <div
      className="grid shrink-0 grid-cols-2 gap-1.5 sm:grid-cols-4"
      role="toolbar"
      aria-label={t("reports.incoming.metricsLabel")}
      data-testid="reports-incoming-summary"
    >
      {INCOMING_REPORT_METRIC_CARDS.map((card) => {
        const selected =
          card.platform == null ? platform == null : platform === card.platform;
        const Icon = card.icon;
        const count = incomingMetricCount(summary, card.key);
        const channelLabel =
          card.platform != null
            ? t(`channel.${channelLabelI18nKey(card.platform)}`)
            : t(card.labelKey);
        return (
          <button
            key={card.key}
            type="button"
            aria-pressed={selected}
            onClick={() => onSelectPlatform(card.platform)}
            className={cn(
              "flex min-h-0 flex-col gap-0.5 rounded-md border px-2 py-1.5 text-start transition-colors",
              selected
                ? "border-accent/40 bg-accent/10"
                : "border-line/70 bg-surface hover:bg-surface-muted",
            )}
            data-testid={`reports-incoming-metric-${card.key}`}
          >
            <span className="flex items-center gap-1 text-[0.65rem] font-medium text-muted">
              {card.platform != null ? (
                <ChannelIdentityBadge
                  platformType={card.platform}
                  size="filter"
                  label={channelLabel}
                />
              ) : (
                <Icon size={12} aria-hidden="true" className="shrink-0" />
              )}
              {t(card.labelKey)}
            </span>
            <span className="text-[1rem] font-semibold tabular-nums text-foreground">
              {count.toLocaleString(i18n.language)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
